import type { CatalogComponent, Node, Layer, NodeGroup, SequenceMessage, SequenceParticipant } from '../../types';
import { STATUS_COLORS } from '../../constants';

interface DiscoveryOverlayProps {
  palette: {
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
  isActive: boolean;
  nodes: Node[];
  groups: NodeGroup[];
  layers: Layer[];
  viewport: { x: number; y: number; scale: number };
  windowSize: { width: number; height: number };
  catalog?: CatalogComponent[];
  messages?: SequenceMessage[];
  participants?: SequenceParticipant[];
}

export function DiscoveryOverlay({
  palette,
  glass,
  isActive,
  nodes,
  groups,
  layers,
  viewport,
  windowSize,
  catalog = [],
  messages = [],
  participants = [],
}: DiscoveryOverlayProps) {
  if (!isActive) return null;

  const visibleNodes = nodes.filter(node => {
    const layer = layers.find(l => l.id === (node.layerId ?? 'default'));
    if (!(layer?.visible ?? true)) return false;
    if (node.parentGroupId) {
      const grp = groups.find(g => g.id === node.parentGroupId);
      if (grp?.collapsed) return false;
    }
    return true;
  });

  const catalogName = (catalogId?: string) =>
    catalog.find(entry => entry.id === catalogId)?.name;

  return (
    <>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
        <div className="bg-indigo-600/90 backdrop-blur border border-indigo-400/40 rounded-xl px-4 py-2 text-xs text-white shadow-xl animate-pulse">
          Inspect — release I to exit · packed info, links, catalog
        </div>
      </div>

      {visibleNodes.map(node => {
        const sx = node.position.x * viewport.scale + viewport.x;
        const sy = node.position.y * viewport.scale + viewport.y;
        if (sx > windowSize.width + 50 || sy > windowSize.height + 50 || sx < -280 || sy < -180) {
          return null;
        }
        const accentColor = (typeof node.data?.color === 'string' ? node.data.color : undefined) ?? '#6366f1';
        const sharedName = catalogName(node.catalogId);
        return (
          <div
            key={`disc-${node.id}`}
            className="fixed z-[400] rounded-lg shadow-xl border backdrop-blur-md px-2.5 py-2 min-w-[140px] max-w-[220px] pointer-events-none"
            style={{ left: sx, top: sy - 56, ...glass }}
          >
            <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: accentColor }}>
              {node.type}
            </div>
            <div className="text-xs font-semibold truncate" style={{ color: palette.panelText }}>
              {node.label}
            </div>
            {sharedName && sharedName !== node.label && (
              <div className="text-[10px] truncate" style={{ color: palette.panelSubText }}>
                Catalog: {sharedName}
              </div>
            )}
            {node.meta?.description && (
              <div className="text-[10px] mt-0.5 line-clamp-2" style={{ color: palette.panelSubText }}>
                {node.meta.description}
              </div>
            )}
            {node.meta?.owner && (
              <div className="text-[10px] truncate" style={{ color: palette.panelSubText }}>
                {node.meta.owner}
              </div>
            )}
            {node.meta?.status && (
              <div className="text-[10px] capitalize" style={{ color: STATUS_COLORS[node.meta.status] ?? '#94a3b8' }}>
                {node.meta.status}
              </div>
            )}
            {node.meta?.links && node.meta.links.length > 0 && (
              <div className="text-[10px] truncate" style={{ color: '#818cf8' }}>
                {node.meta.links.map(link => link.label || link.url).join(' · ')}
              </div>
            )}
          </div>
        );
      })}

      {participants.map(participant => {
        const sx = participant.x * viewport.scale + viewport.x;
        const sy = 40 * viewport.scale + viewport.y;
        if (sx > windowSize.width + 50 || sy > windowSize.height + 50 || sx < -280) return null;
        const sharedName = catalogName(participant.catalogId);
        return (
          <div
            key={`disc-p-${participant.id}`}
            className="fixed z-[400] rounded-lg shadow-xl border backdrop-blur-md px-2.5 py-2 min-w-[140px] max-w-[220px] pointer-events-none"
            style={{ left: sx, top: sy - 8, ...glass }}
          >
            <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: participant.color }}>
              {participant.type}
            </div>
            <div className="text-xs font-semibold truncate" style={{ color: palette.panelText }}>
              {participant.label}
            </div>
            {sharedName && sharedName !== participant.label && (
              <div className="text-[10px]" style={{ color: palette.panelSubText }}>Catalog: {sharedName}</div>
            )}
            {participant.description && (
              <div className="text-[10px] line-clamp-2" style={{ color: palette.panelSubText }}>
                {participant.description}
              </div>
            )}
          </div>
        );
      })}

      {messages.map(message => {
        const source = participants.find(p => p.id === message.sourceId);
        const target = participants.find(p => p.id === message.targetId);
        if (!source || !target) return null;
        const midX = ((source.x + target.x) / 2) * viewport.scale + viewport.x;
        const sy = message.y * viewport.scale + viewport.y;
        if (!message.payload && !message.label) return null;
        if (midX < -80 || sxOffscreen(midX, windowSize.width) || sy < -40 || sy > windowSize.height + 40) {
          return null;
        }
        return (
          <div
            key={`disc-m-${message.id}`}
            className="fixed z-[400] rounded-lg shadow-xl border backdrop-blur-md px-2.5 py-1.5 min-w-[120px] max-w-[220px] pointer-events-none"
            style={{ left: midX, top: sy + 10, ...glass }}
          >
            <div className="text-xs font-semibold truncate" style={{ color: palette.panelText }}>
              {message.label}
            </div>
            {message.payload && (
              <div className="text-[10px] font-mono line-clamp-2" style={{ color: palette.panelSubText }}>
                {message.payload}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function sxOffscreen(x: number, width: number) {
  return x > width + 80;
}
